import { Link } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import type { VenueCard as VenueCardType } from "@/api/types";
import { CoverImage } from "@/components/ui/cover-placeholder";
import { cn } from "@/lib/cn";

interface VenueCardProps {
  venue: VenueCardType;
  onHover?: (id: string | null) => void;
  isHighlighted?: boolean;
}

// fallow-ignore-next-line complexity
function VenueCard({ venue, onHover, isHighlighted }: VenueCardProps) {
  return (
    <Link
      to="/businesses/$slug"
      params={{ slug: venue.slug }}
      className={cn(
        "card card-hover flex flex-col overflow-hidden rounded-md border border-fog bg-surface text-ink no-underline transition-all duration-short ease-default hover:border-electric hover:shadow-[0_0_0_1px_var(--electric)]",
        isHighlighted && "ring-2 ring-electric shadow-soft2 rounded-md",
      )}
      aria-label={[venue.name, venue.primary_category_name, venue.city].filter(Boolean).join(", ")}
      onMouseEnter={() => onHover?.(venue.id)}
      onMouseLeave={() => onHover?.(null)}
    >
      {/* Cover */}
      <CoverImage src={venue.cover_image_url} name={venue.name} decorative loading="lazy" />

      {/* Body */}
      <div className="flex flex-col gap-2 px-[18px] pt-4 pb-[18px]">
        <div className="font-display text-[18px] font-semibold leading-tight text-ink line-clamp-2">
          {venue.name}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {venue.primary_category_name && (
            <span className="chip inline-flex h-8 items-center rounded-pill bg-fog px-3.5 text-[13px] font-medium text-ink">
              {venue.primary_category_name}
            </span>
          )}
          {venue.distance_km != null && (
            <span className="chip inline-flex h-8 items-center rounded-pill bg-fog px-3.5 text-[13px] font-medium text-ink">
              {venue.distance_km.toFixed(1)} km
            </span>
          )}
        </div>

        {venue.rating != null && Number(venue.rating) > 0 && (
          <div className="text-[13px] font-medium text-ink">
            <span className="text-[#f5a623]">★</span> {venue.rating}
            {venue.review_count != null && (
              <span className="text-slate"> ({venue.review_count})</span>
            )}
          </div>
        )}

        {venue.city && (
          <div className="mt-auto flex items-center gap-1 text-[13px] text-ink">
            <MapPin size={13} strokeWidth={2} className="shrink-0 text-slate" />
            {venue.city}
          </div>
        )}
      </div>
    </Link>
  );
}

export { VenueCard, type VenueCardProps };
